import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import "./i18n";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import { LandingPage } from "./pages/LandingPage";
import { AuthPage } from "./pages/AuthPage";
import { BookingPage } from "./pages/BookingPage";
import { ConfirmationPage } from "./pages/ConfirmationPage";
import { DashboardPage } from "./pages/DashboardPage";
import { ModifyBookingPage } from "./pages/ModifyBookingPage";
import { AdminLoginPage } from "./pages/AdminLoginPage";
import { AdminDashboardPage } from "./pages/AdminDashboardPage";
import { VerifyEmailPage } from "./pages/VerifyEmailPage";
import { VerifyPhonePage } from "./pages/VerifyPhonePage";
import { AdminResetPasswordPage } from "./pages/AdminResetPasswordPage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage";
import { EvaluatePage } from "./pages/EvaluatePage";
import { QuickBookingPage } from "./pages/QuickBookingPage";
import { QuickBookingConfirmationPage } from "./pages/QuickBookingConfirmationPage";
import { SeoLandingPage } from "./pages/SeoLandingPage";
import { MentionsLegalesPage } from "./pages/MentionsLegalesPage";
import { CGVPage } from "./pages/CGVPage";
import { ConfidentialitePage } from "./pages/ConfidentialitePage";
import { SuppressionDonneesPage } from "./pages/SuppressionDonneesPage";
import { RappelSuppressionPage } from "./pages/RappelSuppressionPage";
import { DemandeDevisPage } from "./pages/DemandeDevisPage";
import { PWAInstallPrompt } from "./components/PWAInstallPrompt";
import { ClientCenterPage } from "./pages/ClientCenterPage";
import { ClientBookingsPage } from "./pages/ClientBookingsPage";
import { ShareLocationPage } from "./pages/ShareLocationPage";
import { SupportPage } from "./pages/SupportPage";
import { OtpLoginPage } from "./pages/OtpLoginPage";
import { ClientProfilePage } from "./pages/ClientProfilePage";
import ClientInvoicesPage from "./pages/ClientInvoicesPage";
import { RideSummaryPage } from "./pages/RideSummaryPage";
import { AuthCallback } from "./pages/AuthCallback";
import { DriverPortalPage } from "./pages/DriverPortalPage";
import "./App.css";

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  if (!user) {
    const currentPath = window.location.pathname;
    return <Navigate to={`/otp-login?redirect=${encodeURIComponent(currentPath)}`} replace />;
  }
  
  return children;
};

function App() {
  const { i18n } = useTranslation();

  // Track Google Ads landings (once per session/landing-page-load)
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const gclid = params.get('gclid');
      const gadSource = params.get('gad_source');
      const utmSource = params.get('utm_source');
      const utmMedium = params.get('utm_medium');
      const utmCampaign = params.get('utm_campaign');
      const isAdLanding = gclid || gadSource || utmSource === 'google' || utmMedium === 'cpc' || utmMedium === 'paid';
      if (!isAdLanding) return;
      // Idempotency per gclid (don't re-track same gclid on SPA navigation)
      const seenKey = `ad_tracked_${gclid || utmCampaign || 'generic'}`;
      if (sessionStorage.getItem(seenKey)) return;
      sessionStorage.setItem(seenKey, '1');
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/track/ad-click`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          gclid: gclid || '',
          gad_source: gadSource || '',
          utm_source: utmSource || '',
          utm_medium: utmMedium || '',
          utm_campaign: utmCampaign || '',
          referrer: document.referrer || '',
          landing_path: window.location.pathname + window.location.search,
        }),
        keepalive: true,
      }).catch(() => {});
    } catch (e) { /* silent */ }
  }, []);

  // Update <html lang=""> and meta description when language changes
  useEffect(() => {
    const lang = i18n.language || 'fr';
    document.documentElement.lang = lang;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.content = lang === 'en'
        ? "YOUMI GREEN VTC - Premium 100% electric private transport on the French Riviera. Tesla Model Y, professional driver."
        : "YOUMI GREEN VTC - Service de transport prive premium 100% electrique sur la Cote d'Azur et le Var. Tesla Model Y, chauffeur professionnel.";
    }
    const metaTitle = document.querySelector('title');
    if (metaTitle) {
      metaTitle.textContent = lang === 'en'
        ? "YOUMI GREEN VTC - Premium Electric Private Transport"
        : "YOUMI GREEN VTC - Transport Prive Premium 100% Electrique";
    }
  }, [i18n.language]);

  return (
      <AuthProvider>
        <div className="logo-watermark" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/logo-youmi.png)` }} aria-hidden="true" />
        <BrowserRouter>
          <AppRouter />
          <Toaster position="top-right" richColors />
          <PWAInstallPrompt />
        </BrowserRouter>
      </AuthProvider>
  );
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function AppRouter() {
  // Check URL fragment (not query params) for session_id - must be synchronous (before render)
  if (window.location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/quick-booking" element={<QuickBookingPage />} />
      <Route path="/quick-booking/confirmation" element={<QuickBookingConfirmationPage />} />
      <Route path="/vtc-aeroport-nice" element={<SeoLandingPage />} />
      <Route path="/transfert-aeroport-nice-monaco" element={<SeoLandingPage />} />
      <Route path="/transfert-aeroport-nice-cannes" element={<SeoLandingPage />} />
      <Route path="/transfert-aeroport-nice-saint-tropez" element={<SeoLandingPage />} />
      <Route path="/vtc-aeroport-nice-centre" element={<SeoLandingPage />} />
      <Route path="/mentions-legales" element={<MentionsLegalesPage />} />
      <Route path="/cgv" element={<CGVPage />} />
      <Route path="/confidentialite" element={<ConfidentialitePage />} />
      <Route path="/suppression-donnees" element={<SuppressionDonneesPage />} />
      <Route path="/rappel-suppression" element={<RappelSuppressionPage />} />
      <Route path="/demande-devis" element={<DemandeDevisPage />} />
      {/* Client Center */}
      <Route path="/client" element={<ClientCenterPage />} />
      <Route path="/client/profile" element={<ProtectedRoute><ClientProfilePage /></ProtectedRoute>} />
      <Route path="/client/upcoming" element={<ProtectedRoute><ClientBookingsPage /></ProtectedRoute>} />
      <Route path="/client/current" element={<ProtectedRoute><ClientBookingsPage /></ProtectedRoute>} />
      <Route path="/client/history" element={<ProtectedRoute><ClientBookingsPage /></ProtectedRoute>} />
      <Route path="/client/invoices" element={<ProtectedRoute><ClientInvoicesPage /></ProtectedRoute>} />
      <Route path="/share-location" element={<ShareLocationPage />} />
      <Route path="/ride/:bookingId" element={<RideSummaryPage />} />
      <Route path="/chauffeur/:token" element={<DriverPortalPage />} />
      <Route path="/support" element={<SupportPage />} />
      <Route path="/otp-login" element={<OtpLoginPage />} />
      <Route 
        path="/booking" 
        element={
          <ProtectedRoute>
            <BookingPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/booking/confirmation" 
        element={
          <ProtectedRoute>
            <ConfirmationPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/booking/modify/:bookingId" 
        element={
          <ProtectedRoute>
            <ModifyBookingPage />
          </ProtectedRoute>
        } 
      />
      {/* Admin Routes */}
      <Route path="/admin/login" element={<AdminLoginPage />} />
      <Route path="/admin" element={<AdminDashboardPage />} />
      <Route path="/admin/reset-password" element={<AdminResetPasswordPage />} />
      {/* Verification Routes */}
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/verify-phone" element={<VerifyPhonePage />} />
      {/* Password Reset */}
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      {/* Evaluation */}
      <Route path="/evaluate" element={<EvaluatePage />} />
    </Routes>
  );
}

export default App;
